import { Lock, Crown, Star, Zap, Shield, MessageCircle, Check, ExternalLink } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import GlowCard from '@/components/GlowCard';

const proFeatures = [
  'Access to Pro Tools',
  'Priority support',
  'Early access to new tools',
  'Exclusive resources',
];

const eliteFeatures = [
  'Everything in Pro',
  'VIP methods channel access',
  'Personal support line',
  'Premium templates',
  'Advanced features',
];

const Vault = () => {
  const { isPremium, premiumTier, loading } = useProfile();

  const handleTelegramSubscribe = () => {
    window.open('https://t.me/tam_tech786', '_blank');
  };

  const handleWhatsAppSubscribe = () => {
    window.open('https://whatsapp.com/channel/0029VbAozL4FMqrRJcbpJc1K', '_blank');
  };

  const isPro = isPremium && premiumTier === 'pro';
  const isElite = isPremium && premiumTier === 'elite';

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Hero Section */}
      <div className="text-center py-8 glass rounded-2xl border border-border/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-warning/10" />
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold gradient-text">The Vault</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Unlock exclusive premium features, tools, and resources. Choose your tier and
            elevate your experience with TAM Store.
          </p>

          {/* Current Status */}
          {isPremium && (
            <div className="mt-6">
              <Badge 
                className={`text-lg px-4 py-2 ${
                  isElite 
                    ? 'bg-gradient-to-r from-warning to-warning/80 text-warning-foreground' 
                    : 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground'
                }`}
              >
                <Crown className="h-5 w-5 mr-2" />
                You are a TAM {isElite ? 'Elite' : 'Pro'} member!
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Tier Cards */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* TAM Pro */}
        <div className={`glass rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
          isPro 
            ? 'border-primary shadow-lg shadow-primary/20' 
            : 'border-border/50 hover:border-primary/50 hover:-translate-y-1'
        }`}>
          <div className="p-6 border-b border-border/50 bg-gradient-to-r from-primary/10 to-transparent">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Star className="h-6 w-6 text-primary" />
                TAM Pro
              </h2>
              {isPro && (
                <Badge className="bg-primary">Current Plan</Badge>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold">$5</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <ul className="space-y-3">
              {proFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <Check className="h-5 w-5 text-success flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <Button
              onClick={handleTelegramSubscribe}
              className="w-full bg-primary hover:bg-primary/90 transition-transform hover:scale-[1.02]"
              disabled={isPro || isElite}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {isPro ? 'Current Plan' : isElite ? 'You have Elite' : 'Subscribe via Telegram'}
            </Button>
          </div>
        </div>

        {/* TAM Elite */}
        <div className={`glass rounded-2xl border-2 transition-all duration-300 overflow-hidden relative ${
          isElite 
            ? 'border-warning shadow-lg shadow-warning/20' 
            : 'border-border/50 hover:border-warning/50 hover:-translate-y-1'
        }`}>
          <div className="absolute top-4 right-4">
            <Badge className="bg-gradient-to-r from-warning to-warning/80 text-warning-foreground">
              RECOMMENDED
            </Badge>
          </div>
          
          <div className="p-6 border-b border-border/50 bg-gradient-to-r from-warning/10 to-transparent">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Crown className="h-6 w-6 text-warning" />
                TAM Elite
              </h2>
              {isElite && (
                <Badge className="bg-warning text-warning-foreground">Current Plan</Badge>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold">$15</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <ul className="space-y-3">
              {eliteFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <Check className="h-5 w-5 text-warning flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <Button
              onClick={handleWhatsAppSubscribe}
              className="w-full bg-gradient-to-r from-warning to-warning/80 hover:from-warning/90 hover:to-warning/70 text-warning-foreground transition-transform hover:scale-[1.02]"
              disabled={isElite}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {isElite ? 'Current Plan' : 'Subscribe via WhatsApp'}
            </Button>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold text-center mb-6">Why Go Premium?</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <GlowCard className="p-6 text-center">
            <Zap className="h-10 w-10 text-primary mx-auto mb-3" />
            <h4 className="font-semibold mb-2">Exclusive Tools</h4>
            <p className="text-sm text-muted-foreground">
              Access premium-only tools and features not available to free users.
            </p>
          </GlowCard>
          <GlowCard className="p-6 text-center">
            <Shield className="h-10 w-10 text-primary mx-auto mb-3" />
            <h4 className="font-semibold mb-2">Priority Support</h4>
            <p className="text-sm text-muted-foreground">
              Get faster responses and dedicated assistance from our team.
            </p>
          </GlowCard>
          <GlowCard className="p-6 text-center">
            <Crown className="h-10 w-10 text-warning mx-auto mb-3" />
            <h4 className="font-semibold mb-2">VIP Access</h4>
            <p className="text-sm text-muted-foreground">
              Get early access to new features and exclusive content.
            </p>
          </GlowCard>
        </div>
      </div>
    </div>
  );
};

export default Vault;
